import React, { useState, useEffect } from "react";
import { FiX, FiCheck, FiUser } from "react-icons/fi";
import { getThemeColors, Theme } from "../theme/colors";

interface User {
  username: string;
  id: number;
}

interface UserSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onShare: (selectedUsers: string[]) => void;
  users: User[];
  isLoading: boolean;
  colorMode: Theme;
  username: string;
}

const UserSelectionPopup: React.FC<UserSelectionPopupProps> = ({
  isOpen,
  onClose,
  onShare,
  users,
  isLoading,
  colorMode,
  username,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const currentColors = getThemeColors(username, colorMode);

  useEffect(() => {
    if (!isOpen) {
      setSelectedUsers([]);
      setSearchTerm("");
    }
  }, [isOpen]);

  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUserToggle = (userUsername: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userUsername)
        ? prev.filter((u) => u !== userUsername)
        : [...prev, userUsername]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((user) => user.username));
    }
  };

  const handleShare = () => {
    if (selectedUsers.length > 0) {
      onShare(selectedUsers);
    }
  };

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const popupStyle: React.CSSProperties = {
    backgroundColor: currentColors.background,
    border: `1px solid ${currentColors.border}`,
    borderRadius: "8px",
    padding: "24px",
    maxWidth: "500px",
    width: "90%",
    maxHeight: "600px",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    borderBottom: `1px solid ${currentColors.border}`,
    paddingBottom: "12px",
  };

  const searchStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    border: `1px solid ${currentColors.border}`,
    borderRadius: "4px",
    backgroundColor: currentColors.background,
    color: currentColors.text,
    marginBottom: "16px",
  };

  const userListStyle: React.CSSProperties = {
    maxHeight: "300px",
    overflowY: "auto",
    border: `1px solid ${currentColors.border}`,
    borderRadius: "4px",
    marginBottom: "16px",
  };

  const userItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    borderBottom: `1px solid ${currentColors.border}`,
    cursor: "pointer",
    transition: "background-color 0.2s",
  };

  const checkboxStyle: React.CSSProperties = {
    marginRight: "12px",
    width: "18px",
    height: "18px",
    border: `2px solid ${currentColors.border}`,
    borderRadius: "3px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  const buttonStyle: React.CSSProperties = {
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s",
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: currentColors.buttonHover,
    color: currentColors.background,
    marginLeft: "8px",
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "transparent",
    color: currentColors.text,
    border: `1px solid ${currentColors.border}`,
  };

  const selectAllButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "transparent",
    color: currentColors.text,
    border: `1px solid ${currentColors.border}`,
    marginBottom: "12px",
    fontSize: "12px",
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0, color: currentColors.text }}>
            Share Chat with Users
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: currentColors.text,
              padding: "4px",
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={searchStyle}
        />

        <button
          onClick={handleSelectAll}
          style={selectAllButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = currentColors.buttonHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          {selectedUsers.length === filteredUsers.length
            ? "Deselect All"
            : "Select All"}
        </button>

        <div style={userListStyle}>
          {isLoading ? (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: currentColors.text,
              }}
            >
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: currentColors.text,
              }}
            >
              No users found
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                style={userItemStyle}
                onClick={() => handleUserToggle(user.username)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    currentColors.buttonHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <div
                  style={{
                    ...checkboxStyle,
                    backgroundColor: selectedUsers.includes(user.username)
                      ? currentColors.buttonHover
                      : "transparent",
                  }}
                >
                  {selectedUsers.includes(user.username) && (
                    <FiCheck size={12} color={currentColors.background} />
                  )}
                </div>
                <FiUser
                  size={16}
                  style={{ marginRight: "8px", color: currentColors.text }}
                />
                <span style={{ color: currentColors.text }}>
                  {user.username}
                </span>
              </div>
            ))
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ color: currentColors.text, fontSize: "14px" }}>
            {selectedUsers.length} user{selectedUsers.length !== 1 ? "s" : ""}{" "}
            selected
          </span>
          <div>
            <button
              onClick={onClose}
              style={secondaryButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  currentColors.buttonHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleShare}
              disabled={selectedUsers.length === 0}
              style={{
                ...primaryButtonStyle,
                opacity: selectedUsers.length === 0 ? 0.5 : 1,
                cursor: selectedUsers.length === 0 ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (selectedUsers.length > 0) {
                  e.currentTarget.style.backgroundColor =
                    currentColors.optionsHover;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedUsers.length > 0) {
                  e.currentTarget.style.backgroundColor =
                    currentColors.buttonHover;
                }
              }}
            >
              Share ({selectedUsers.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSelectionPopup;
